import React, { useMemo } from "react";
import { motion } from "framer-motion";

/**
 * Lightweight confetti burst — no external library.
 *
 * Renders 14 colored particles that shoot outward from the center in random directions,
 * spin, and fade over 800ms. Pointer-events-none so it never blocks clicks under it.
 *
 * Cheap: pure CSS transforms + framer-motion for animation. Mount only when celebrating —
 * unmount immediately after; there's no persistent animation loop.
 *
 * Props:
 *   - keyId  a unique id (e.g. `${task.id}-${Date.now()}`) so React remounts the burst on
 *            every celebration instead of reusing a stale one.
 *   - size   overall diameter of the burst in px; defaults to 180.
 */
export const ConfettiBurst = ({ keyId, size = 180 }) => {
    // Precompute particle configs once per mount — random-but-stable across the 800ms life.
    const particles = useMemo(() => {
        const COLORS = ["#22d3ee", "#d946ef", "#facc15", "#4ade80", "#f472b6", "#a78bfa"];
        return Array.from({ length: 14 }, (_, i) => {
            const angle = (Math.PI * 2 * i) / 14 + (Math.random() - 0.5) * 0.6;
            const distance = size * (0.35 + Math.random() * 0.35);
            return {
                id: i,
                color: COLORS[i % COLORS.length],
                dx: Math.cos(angle) * distance,
                dy: Math.sin(angle) * distance - 6,        // slight upward lift
                rotate: (Math.random() - 0.5) * 360 * 2,   // random spin
                shape: i % 3 === 0 ? "square" : "circle",  // mix shapes for texture
            };
        });
    }, [keyId, size]);

    return (
        <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-visible"
            aria-hidden="true"
            data-testid="confetti-burst"
        >
            {particles.map((p) => (
                <motion.div
                    key={p.id}
                    className={p.shape === "circle" ? "rounded-full" : "rounded-[2px]"}
                    style={{
                        position: "absolute",
                        width: 7,
                        height: 7,
                        backgroundColor: p.color,
                        boxShadow: `0 0 6px ${p.color}`,
                    }}
                    initial={{ x: 0, y: 0, opacity: 1, rotate: 0, scale: 0.6 }}
                    animate={{ x: p.dx, y: p.dy, opacity: 0, rotate: p.rotate, scale: 1 }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                />
            ))}
        </div>
    );
};

export default ConfettiBurst;
